import React, { useState, useRef, } from 'react';
import { Box, Image, Text, useBreakpointValue } from '@chakra-ui/react'
import { useClickOutside } from '../../../hooks/useClickOutside';
import { useEffectEvent } from '../../../hooks/useEffectEvent';
import DraggableProgressbar from '../../../components/ui/draggableProgressBar';

export type Variant =
  | 'module'
  | 'deep-dive'
  | 'home-work-audio'
  | 'home-work-video'
  | 'review-video'
  | 'live-video'
  | 'shorts'
  | 'guided-practice'
  | 'checkIn-review-video'
  | 'home-work-video';

export const calculateProgressPercentage = (
  currentTime: number,
  duration: number,
) => {
  if (duration !== null && duration !== 0) {
    return (currentTime / duration) * 100;
  }
  return 0;
};

export default function VideoControlBar({
  isPlaying,
  buttonFaded,
  isMuted,
  isFullScreen,
  toggleFullScreen,
  duration,
  currentTime,
  bufferedPercentage,
  toggleMute,
  handlePlayOrPause,
  variant,
  seekVideo,
  hideFullScreen,
  hideDragProgBar,
  hidePlayPauseIcon,
  onClick,
  id,
  isSadhguruBgVideo,
  playerInstanceRef
}) {
  const formatDuration = useEffectEvent((seconds: number) => {
    const roundedSeconds = Math.round(seconds);
    const hours = Math.floor(roundedSeconds / 3600);
    const minutes = Math.floor((roundedSeconds % 3600) / 60);
    const remainingSeconds = roundedSeconds % 60;
    const paddedMinutes = minutes < 10 && hours > 0 ? `0${minutes}` : minutes;
    const paddedSeconds =
      remainingSeconds < 10 ? `0${remainingSeconds}` : remainingSeconds;
    if (hours > 0) {
      return `${hours}:${paddedMinutes}:${paddedSeconds}`;
    } else {
      return `${minutes}:${paddedSeconds}`;
    }
  });

  // for dropdown menu
  const [isOpen, setIsOpen] = useState(false);
  const videQualityRef = useRef<HTMLDivElement | null>(null);
  const videoQualityButtonRef = useRef<HTMLDivElement | null>(null);
  const touchHandledRef = useRef(false);

  useClickOutside(videQualityRef, videoQualityButtonRef, isOpen, setIsOpen);

  // Helper function to handle both touch and click events without double-firing
  const createInteractionHandler = (handler: () => void) => {
    return {
      onTouchEnd: (e: React.TouchEvent) => {
        e.stopPropagation();
        e.preventDefault();
        touchHandledRef.current = true;
        handler();
        // Reset after a short delay to allow click event to be prevented
        setTimeout(() => {
          touchHandledRef.current = false;
        }, 300);
      },
      onClick: (e: React.MouseEvent) => {
        e.stopPropagation();
        // Prevent click if touch was just handled
        if (touchHandledRef.current) {
          e.preventDefault();
          return;
        }
        handler();
      }
    };
  };
  let playPauseBtnSize = useBreakpointValue({ base: '80px', md: '90px', '2xl': '120px' })
  let soundIconSize = useBreakpointValue({ base: '40px', '2xl': '52px' })

  const completedPercentage = calculateProgressPercentage(
    currentTime,
    duration,
  );
  const isDraggableControls =
    process.env.NEXT_PUBLIC_ENABLE_DEV_CONTROLS === 'true'
      ? true
      : variant === 'review-video' ||
      variant === 'deep-dive' ||
      variant === 'guided-practice' ||
      variant === 'checkIn-review-video' ||
      variant === 'home-work-video';
  return (
    <Box
    >
      <Box
        display={'flex'} justifyContent={'center'}
        alignItems={'center'}
      >
        {onClick ?
          <Box
            className={`${id}-onClickkk`}
            position={'absolute'}
            backgroundColor={'transparent'}
            top={0}
            transform={'translateY(0%)'}
            height={'100%'}
            width={'100%'}
            cursor={'pointer'}
            onClick={onClick}
          /> : null}
        <Box>
          {hidePlayPauseIcon ? null :
            <Box
              style={{
                opacity: buttonFaded ? 0 : 1,
                transition: 'opacity 0.3s ease',
                backfaceVisibility: 'hidden', // Force layer promotion for iOS
              }}
              onClick={(e) => e.stopPropagation()} pos={'absolute'} top={{ base: '47%', md: '48%' }}
              left={{ base: '47%', md: "48%" }}
              transform={{ base: 'translate(-47%,-47%)', md: 'translate(-48%,-48%)' }}
              display={'flex'}
              w={'auto'} >
              {isSadhguruBgVideo ? <Image width={playPauseBtnSize} height={playPauseBtnSize} cursor={'pointer'}
                onClick={() => {
                  playerInstanceRef.current?.video?.pause();
                  seekVideo(0);
                  playerInstanceRef.current?.video?.play();
                  toggleMute();
                  onClick();
                }} src={'https://static.sadhguru.org/d/46272/1758777174-playiconvdocipher.png'} alt='play'
                sx={{
                  '@media (hover: hover)': {
                    '&:hover': {
                      content: 'url(https://static.sadhguru.org/d/46272/1761721429-playhover.svg)',
                    },
                  },
                }}
              /> :
                isPlaying ? (
                  <Image width={playPauseBtnSize} height={playPauseBtnSize} cursor={'pointer'}
                    onClick={handlePlayOrPause}
                    src={'https://static.sadhguru.org/d/46272/1758777174-pauseiconvdocipher.png'} alt='pause'
                    sx={{
                      '@media (hover: hover)': {
                        '&:hover': {
                          content: 'url(https://static.sadhguru.org/d/46272/1761721429-pausehover.svg)',
                        },
                      },
                    }}
                  />
                ) : (
                  <Image width={playPauseBtnSize} height={playPauseBtnSize} cursor={'pointer'}
                    onClick={handlePlayOrPause} src={'https://static.sadhguru.org/d/46272/1758777174-playiconvdocipher.png'} alt='play'
                    sx={{
                      '@media (hover: hover)': {
                        '&:hover': {
                          content: 'url(https://static.sadhguru.org/d/46272/1761721429-playhover.svg)',
                        },
                      },
                    }}
                  />
                )}
            </Box>
          }
          {/* <div > */}
          <Box mt={isFullScreen ? {base: "-2.5rem", md: 'unset'} : 'unset'}  onClick={(e) => e.stopPropagation()} pos={'absolute'} top={{ base: '99%' }} transform={'translateY(-98%)'} 
            px={isFullScreen ? {base: '4%', md: '1.5%'} : '1.5%'}
            left={'0'} display={'flex'} width={'100%'} justifyContent={'space-between'} alignItems={'center'}
            zIndex={1} >
            {hideDragProgBar ? null :
              <Text
                fontFamily={'FedraSansStd-book'}
                fontSize={{ base: '14px', md: '16px', '2xl': '18px' }} fontWeight={400} color={'#FFFFFF'}
              >{formatDuration(currentTime)} / {formatDuration(duration)}</Text>}

            {hideFullScreen ? null :
              <Box>
                {isMuted ? (<>
                  <Image 
                    width={soundIconSize} 
                    height={soundIconSize} 
                    {...createInteractionHandler(toggleMute)}
                    src={'https://static.sadhguru.org/d/46272/1758777174-muteiconvdocipher.png'} 
                    alt='mute'
                    cursor="pointer"
                    sx={{
                      '@media (hover: hover)': {
                        '&:hover': {
                          background: "#FFFFFF33",
                          borderRadius: '100%'
                        }
                      },
                      '@media (hover: none)': {
                        '&:active': {
                          background: "#FFFFFF33",
                          borderRadius: '100%'
                        }
                      }
                    }}
                  />
                  {isFullScreen ?
                    <Image 
                      width={soundIconSize} 
                      height={soundIconSize} 
                      {...createInteractionHandler(toggleFullScreen)}
                      src='https://static.sadhguru.org/d/46272/1758780482-minimizeiconvdocipher.png' 
                      alt='minimize'
                      cursor="pointer"
                      sx={{
                        '@media (hover: hover)': {
                          '&:hover': {
                            background: "#FFFFFF33",
                            borderRadius: '100%'
                          }
                        },
                        '@media (hover: none)': {
                          '&:active': {
                            background: "#FFFFFF33",
                            borderRadius: '100%'
                          }
                        }
                      }}
                    />
                    : <Image 
                      width={soundIconSize} 
                      height={soundIconSize} 
                      {...createInteractionHandler(toggleFullScreen)}
                      src={'https://static.sadhguru.org/d/46272/1758777174-fullscreeniconvdocipher.png'} 
                      alt='expand'
                      cursor="pointer"
                      sx={{
                        '@media (hover: hover)': {
                          '&:hover': {
                            background: "#FFFFFF33",
                            borderRadius: '100%'
                          }
                        },
                        '@media (hover: none)': {
                          '&:active': {
                            background: "#FFFFFF33",
                            borderRadius: '100%'
                          }
                        }
                      }}
                    />}
                </>
                ) : (<>
                  <Image 
                    width={soundIconSize} 
                    height={soundIconSize} 
                    {...createInteractionHandler(toggleMute)}
                    src={'https://static.sadhguru.org/d/46272/1758777174-soundiconvdocipher.png'} 
                    alt='volume'
                    cursor="pointer"
                    sx={{
                      '@media (hover: hover)': {
                        '&:hover': {
                          background: "#FFFFFF33",
                          borderRadius: '100%'
                        }
                      },
                      '@media (hover: none)': {
                        '&:active': {
                          background: "#FFFFFF33",
                          borderRadius: '100%'
                        }
                      }
                    }}
                  />
                  {isFullScreen ?
                    <Image 
                      width={soundIconSize} 
                      height={soundIconSize} 
                      {...createInteractionHandler(toggleFullScreen)}
                      src='https://static.sadhguru.org/d/46272/1758780482-minimizeiconvdocipher.png' 
                      alt='minimize'
                      cursor="pointer"
                      sx={{
                        '@media (hover: hover)': {
                          '&:hover': {
                            background: "#FFFFFF33",
                            borderRadius: '100%'
                          }
                        },
                        '@media (hover: none)': {
                          '&:active': {
                            background: "#FFFFFF33",
                            borderRadius: '100%'
                          }
                        }
                      }}
                    />
                    : <Image 
                      width={soundIconSize} 
                      height={soundIconSize} 
                      {...createInteractionHandler(toggleFullScreen)}
                      src={'https://static.sadhguru.org/d/46272/1758777174-fullscreeniconvdocipher.png'} 
                      alt='expand'
                      cursor="pointer"
                      sx={{
                        '@media (hover: hover)': {
                          '&:hover': {
                            background: "#FFFFFF33",
                            borderRadius: '100%'
                          }
                        },
                        '@media (hover: none)': {
                          '&:active': {
                            background: "#FFFFFF33",
                            borderRadius: '100%'
                          }
                        }
                      }}
                    />}
                </>
                )}
              </Box>}
          </Box>
          {hideDragProgBar ? null :
            <Box onClick={(e) => e.stopPropagation()} display={'flex'} width={'100%'} left={'0'}
              position={'absolute'} bottom={isFullScreen ? { base: '2rem', md: '0' } : '0'} >
              <Box
                background={`linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, #000000 100%)`}
                width={'100%'}
                height={{ base: '90px', md: '104px' }} position={'absolute'} bottom={isFullScreen ? { base: '-2rem', md: 0 } : 0} zIndex={0} />              
                <DraggableProgressbar
                  completedPercentage={completedPercentage}
                  bufferedPercentage={bufferedPercentage}
                  duration={duration}
                  onSeek={seekVideo}
                  isFullScreen={isFullScreen}
                />
            </Box>
          }
        </Box>
      </Box>
    </Box>
  );
}
